import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as ActionCreators from '../../redux/action';
import OffersTableSC, {
  PaginationWrapSC,
  AdditionalTopSC,
  EmptySC,
  LoadingWrapper,
} from './offers-table.styled';
import { BodyText /*  SmallText */, SmallText } from '../typography';
import OffersItem from './offers-item';
import { ReactComponent as LeftArrow } from '../../assets/img/svg/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/img/svg/right-arrow.svg';
import dlt from '../../utils/default-lang';
import { FormattedMessage } from 'react-intl';
import useWindowSize from '../../utils/screen-size';
import { BP_TABLET_MIN } from '../../assets/styles/breakpoints';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import WishmakerModal from '../ui/modal/modal';
import SmsVerification from '../sms-verification';
import SuccessModal from '../success-modal';
import TableSellType from './components/table-sell-type';
import InlineLoading from '../ui/inline-loading';

const ITEMS_PER_PAGE = 10;

const _OffersTable = ({
  maxRows,
  hasPagination,
  hasAdditionalTop,
  hasLinkOnAll,
  sendSms,
  cancelOfferSell,
  history,
  getInvestorOffers,
  offers,
  getInvestorOffersCount,
  setInvestorOffers,
}) => {
  const screenSize = useWindowSize();
  const isTablet = screenSize.width < BP_TABLET_MIN;

  const [additionalData, setAdditionalData] = useState({});
  const [isSmsModalOpened, setSmsModalOpened] = useState(false);
  const [isSuccessModalOpened, setSuccessModalOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSmsCodeError, setIsSmsCodeError] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sellType, setSellType] = useState('offered-manual');
  const firstLoaded = React.useRef(false);

  const { pagination, items, loading } = React.useMemo(() => {
    return sellType === 'offered-manual'
      ? { ...offers.manualOffers, loading: offers.manualLoading }
      : { ...offers.automaticOffers, loading: offers.automaticLoading };
  }, [sellType, offers]);

  const fetch = React.useCallback(() => {
    let reqData = {
      type: sellType,
      per_page: ITEMS_PER_PAGE,
    };

    const onBadResponse = () => {
      console.log('error');
    };

    const onSuccess = () => {};

    getInvestorOffers({ data: reqData, onSuccess, onBadResponse });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellType]);

  /* Call data on start */
  useEffect(() => {
    fetch();
  }, [fetch]);

  const formattedData = React.useMemo(() => {
    if (maxRows) {
      return items.slice(0, maxRows) || [];
    } else {
      return items || [];
    }
  }, [maxRows, items]);

  React.useEffect(() => {
    if (!firstLoaded.current) {
      if (offers.typesCounts.manual === 0 && offers.typesCounts.automatic > 0) {
        setSellType('offered-automatic');
        firstLoaded.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers.typesCounts, firstLoaded]);

  React.useEffect(() => {
    getInvestorOffersCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Table pagination */
  const onArrowClick = (type) => {
    let formattedData = {
      per_page: ITEMS_PER_PAGE,
      type: sellType,
    };
    switch (type) {
      case 'next':
        formattedData = {
          page: (pagination.current_page += 1),
          ...formattedData,
        };
        break;
      case 'back':
        formattedData = {
          page: (pagination.current_page -= 1),
          ...formattedData,
        };
        break;
      default:
        break;
    }

    const onBadResponse = () => {
      setIsLoading(true);
      formattedData = {
        type: sellType,
        page: pagination.current_page,
        ...formattedData,
      };
      setTimeout(() => {
        sendTablePagination();
      }, 10000);
    };

    const onSuccess = () => {
      setIsLoading(false);
    };

    const sendTablePagination = async () => {
      await getInvestorOffers({ data: formattedData, onSuccess, onBadResponse });
      setIsLoading(true);
    };
    sendTablePagination();
  };

  /* Send sms */
  const onSmsSend = (smsType) => {
    sendSms({ type: smsType });
  };

  /* On cancel offer click */
  const onCancelClick = (event, offerId) => {
    event.stopPropagation();
    onSmsSend('cancel-offer');
    setSmsModalOpened(true);
    setAdditionalData({ offerId: offerId });
  };

  /* SMS Confirmation of cancel offer call */
  const onSmsModalSubmit = (smsModalData) => {
    const onBadSmsCode = () => {
      setIsSending(false);
      setIsSmsCodeError(true);
    };
    const onSuccess = () => {
      setIsSending(false);
      setSmsModalOpened(false);
      setSuccessModalOpened(true);
    };
    const onError = () => {
      setIsSending(false);
    };

    const onCancelOffer = async () => {
      await cancelOfferSell({
        data: { sms_code: smsModalData.sms_code },
        offer: smsModalData.offerId,
        onSuccess,
        onBadSmsCode,
        onError,
      });
      setIsSending(true);
    };
    onCancelOffer();
  };

  /* Call refreshed data after success call */
  const callRefreshedData = () => {
    let formattedData = {
      type: sellType,
      per_page: ITEMS_PER_PAGE,
    };

    const onBadResponse = () => {
      console.log('error');
    };

    const onSuccess = () => {
      setIsLoading(false);
    };

    getInvestorOffers({ data: formattedData, onSuccess, onBadResponse });
  };

  /* After success modal close call refresh data */
  const onCloseSuccess = () => {
    setSuccessModalOpened(false);
    callRefreshedData();
  };

  function renderContent() {
    if (loading && formattedData.length === 0) {
      return (
        <LoadingWrapper>
          <InlineLoading />
        </LoadingWrapper>
      );
    }

    if (!loading && formattedData.length === 0) {
      return (
        <EmptySC>
          <SmallText className="black-transparent bold">
            {sellType === 'offered-manual' ? (
              <FormattedMessage
                id="investor_offers_table_empty_manual"
                defaultMessage={dlt['investor_offers_table_empty_manual']}
              />
            ) : (
              <FormattedMessage
                id="investor_offers_table_empty_automatic"
                defaultMessage={dlt['investor_offers_table_empty_automatic']}
              />
            )}
          </SmallText>
        </EmptySC>
      );
    }

    return (
      <>
        <OffersTableSC>
          {isTablet ? null : (
            <thead>
              <tr>
                <th>
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="investor_offers_table_head_1"
                      defaultMessage={dlt['investor_offers_table_head_1']}
                    />
                  </SmallText>
                </th>
                <th>
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="investor_offers_table_head_2"
                      defaultMessage={dlt['investor_offers_table_head_2']}
                    />
                  </SmallText>
                </th>
                <th>
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="investor_offers_table_head_3"
                      defaultMessage={dlt['investor_offers_table_head_3']}
                    />
                  </SmallText>
                </th>
                <th>
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="investor_offers_table_head_4"
                      defaultMessage={dlt['investor_offers_table_head_4']}
                    />
                  </SmallText>
                </th>
                <th>
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="investor_offers_table_head_5"
                      defaultMessage={dlt['investor_offers_table_head_5']}
                    />
                  </SmallText>
                </th>
                {sellType === 'offered-manual' && (
                  <>
                    <th></th>
                    <th></th>
                  </>
                )}
                <th></th>
              </tr>
            </thead>
          )}

          <tbody>
            {formattedData.map((item) => {
              return (
                <OffersItem
                  key={item.id}
                  id={item.id}
                  hasPagination={hasPagination}
                  name={item.debtor.wmNumber}
                  payments={item.loan.paidInstallmentCount}
                  paymentsTotal={item.loan.totalInstallmentCount}
                  amount={Number(item.amount)}
                  date={item.loan.loanDueAt}
                  status={item.isSold ? 'sale' : item.loan.daysInArrears > 0 ? 'late' : 'done'}
                  inArrear={item.loan.daysInArrears}
                  isTablet={isTablet}
                  onCancelClick={(event) => onCancelClick(event, item.loan.offer_id)}
                  hasBiddings={item.biddings_active}
                  biddingsCount={item.biddings_count}
                  sellType={sellType}
                />
              );
            })}
          </tbody>
        </OffersTableSC>

        {hasPagination ? (
          <PaginationWrapSC>
            <BodyText className="bolder orange">
              {pagination.from}-{pagination.to}{' '}
              <FormattedMessage id="global_from" defaultMessage={dlt['global_from']} />{' '}
              {pagination.total}
            </BodyText>
            <div className="arrow-wrap">
              <button
                disabled={pagination.current_page < 2 || isLoading || offers.offersLoading}
                onClick={() => onArrowClick('back')}
              >
                <LeftArrow />
              </button>
              <button
                disabled={
                  pagination.current_page === pagination.last_page ||
                  isLoading ||
                  offers.offersLoading
                }
                onClick={() => onArrowClick('next')}
              >
                <RightArrow />
              </button>
            </div>
          </PaginationWrapSC>
        ) : null}
      </>
    );
  }

  return (
    <>
      <WishmakerModal
        isOpened={isSuccessModalOpened}
        onClose={() => onCloseSuccess()}
        className="special"
        withCross
      >
        <SuccessModal headlineText="modal_success_offer_canceled"></SuccessModal>
      </WishmakerModal>

      <WishmakerModal
        isOpened={isSmsModalOpened}
        onClose={() => setSmsModalOpened(false)}
        className="special"
        withCross
      >
        <SmsVerification
          onSubmit={(data) => onSmsModalSubmit(data)}
          additionalData={additionalData}
          isError={isSmsCodeError}
          isSending={isSending}
          onResend={() => onSmsSend('cancel-offer')}
          onBadSms={() => setIsSmsCodeError(false)}
        />
      </WishmakerModal>

      {hasAdditionalTop ? (
        <AdditionalTopSC>
          <BodyText className="bolder">
            <FormattedMessage
              id="investor_offers_table_headline"
              defaultMessage={dlt['investor_offers_table_headline']}
            />
          </BodyText>
          <div className="line-separator"></div>
        </AdditionalTopSC>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <TableSellType
          value={sellType}
          onChange={(type) => {
            const payload = { data: [], payload: {} };
            setInvestorOffers(
              sellType === 'offered-manual'
                ? { manualOffers: payload, manualLoading: true }
                : { automaticOffers: payload, automaticLoading: true }
            );
            setSellType(type);
          }}
        />
      </div>

      {renderContent()}
    </>
  );
};

_OffersTable.propTypes = {
  maxRows: PropTypes.number,
  hasPagination: PropTypes.bool,
  hasAdditionalTop: PropTypes.bool,
};

_OffersTable.defaultProps = {
  maxRows: null,
  hasPagination: false,
  hasAdditionalTop: false,
};

const mapStateToProps = (state) => {
  return {
    offers: state.offers,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      sendSms: ActionCreators.sendSms,
      getInvestorOffers: ActionCreators.getInvestorOffers,
      cancelOfferSell: ActionCreators.cancelOfferSell,
      getInvestorOffersCount: ActionCreators.getInvestorOffersCount,
      setInvestorOffers: ActionCreators.setInvestorOffers,
    },
    dispatch
  );
};

const OffersTable = withRouter(connect(mapStateToProps, mapDispatchToProps)(_OffersTable));

export default OffersTable;
